import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { ApplicationRef, inject, Injectable } from '@angular/core';
import { Socket } from 'ngx-socket-io';

import { environment } from '../environments/environment';
import { AuthSyncService } from '../app/auth-sync.service';

/**
 * `/org-subscription` handshake: JWT + orgId.
 *
 * Do not use `take(1)` on `$user`: the first emission is often from localStorage before
 * `refreshProfile()` fills `UserOrganizations`, so orgId would be missing and the socket
 * would never connect. Wait until orgId exists; reconnect only when orgId changes.
 */
@Injectable()
export class OrgSubscriptionSocket extends Socket {
  private connectedOrgId: number | null = null;

  constructor(readonly authSyncService: AuthSyncService) {
    super(
      {
        url: `${environment.BASE_PATH}/org-subscription`,
        options: {
          autoConnect: false,
          transports: ['websocket'],
        },
      },
      inject(ApplicationRef)
    );

    this.authSyncService.$user
      .pipe(
        map(user => user?.UserOrganizations?.[0]?.orgId),
        filter((orgId): orgId is number => typeof orgId === 'number' && orgId > 0),
        distinctUntilChanged()
      )
      .subscribe(orgId => {
        this.ensureConnected(orgId);
      });
  }

  /**
   * Connect (or reconnect) with the given org. Call from screens that know the authoritative
   * org id from the API (e.g. subscription page) so the socket matches the loaded org.
   */
  ensureConnected(orgId: number): void {
    const token = this.authSyncService.getToken();
    if (!token || orgId == null || orgId <= 0) {
      return;
    }
    if (this.ioSocket.connected && this.connectedOrgId === orgId) {
      return;
    }
    if (this.ioSocket.connected) {
      this.disconnect();
    }
    const orgIdStr = String(orgId);
    this.ioSocket.io.opts.query = {
      token,
      orgId: orgIdStr,
    };
    this.ioSocket.auth = {
      token,
      orgId: orgIdStr,
    };
    this.connectedOrgId = orgId;
    this.connect();
  }

  override disconnect() {
    this.connectedOrgId = null;
    return super.disconnect();
  }
}

